import type { WorkspaceDocument, WorkspaceId } from '@/types';
import { PERSISTENCE_SCHEMA_VERSION } from '@/constants/storage';

export interface PersistedSnapshot {
  readonly version: number;
  readonly savedAt: number;
  readonly activeWorkspaceId: WorkspaceId;
  readonly workspaces: Readonly<Record<WorkspaceId, WorkspaceDocument>>;
}

export const serialize = (snapshot: Omit<PersistedSnapshot, 'version' | 'savedAt'>, now = Date.now()): string =>
  JSON.stringify({ version: PERSISTENCE_SCHEMA_VERSION, savedAt: now, ...snapshot } satisfies PersistedSnapshot);

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * Structural validation of untrusted localStorage content. We never assume the
 * shape is intact: a browser extension, a manual edit, or an older build could
 * have written it. Anything malformed is rejected rather than partially applied.
 */
const isDocument = (v: unknown): v is WorkspaceDocument => {
  if (!isRecord(v) || !isRecord(v.workspace) || !isRecord(v.objects) || !isRecord(v.connections)) return false;
  const ws = v.workspace;
  if (typeof ws.id !== 'string' || typeof ws.name !== 'string' || !isRecord(ws.camera)) return false;
  const cam = ws.camera;
  if (typeof cam.zoom !== 'number' || !isRecord(cam.position)) return false;
  for (const obj of Object.values(v.objects)) {
    if (!isRecord(obj) || typeof obj.id !== 'string' || typeof obj.kind !== 'string') return false;
    if (!isRecord(obj.transform) || !isRecord(obj.size)) return false;
  }
  return true;
};

export const deserialize = (raw: string | null): PersistedSnapshot | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.version !== 'number') return null;
  if (parsed.version !== PERSISTENCE_SCHEMA_VERSION) return migrate(parsed);
  if (!isRecord(parsed.workspaces) || typeof parsed.activeWorkspaceId !== 'string') return null;
  const workspaces: Record<string, WorkspaceDocument> = {};
  for (const [id, doc] of Object.entries(parsed.workspaces)) {
    if (!isDocument(doc)) return null;
    workspaces[id] = doc;
  }
  return {
    version: parsed.version,
    savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    activeWorkspaceId: parsed.activeWorkspaceId as WorkspaceId,
    workspaces,
  };
};

/** No prior schema versions exist yet; unknown versions are discarded. */
const migrate = (_snapshot: Record<string, unknown>): PersistedSnapshot | null => null;
